/**
 * Machine ID Attestation Flow
 * 
 * Orchestrates the complete attestation flow after user login
 */

import * as Sentry from '@sentry/react';
import { machineIdAttestationState } from '../stores/machineIdAttestationState';
import { performAttestation } from './machineIdAttestation';
import { getMachineIdAttestationData, checkAttestationValidity, storeAttestationMetadata } from './machineIdHelpers';
import { serviceContainer } from '../../services/ServiceContainer';

/**
 * Run the attestation flow after successful Clerk login
 * 
 * This function:
 * 1. Checks if attestation is needed
 * 2. If needed, performs the complete attestation flow
 * 3. Updates the attestation state accordingly
 * 
 * @param clerkUserId - Clerk user ID
 * @param clerkToken - Clerk authentication token
 */
export async function runAttestationFlow(
  clerkUserId: string,
  clerkToken: string,
): Promise<void> {
  try {
    console.log('[AttestationFlow] Starting attestation flow for user:', clerkUserId);
    
    machineIdAttestationState.updateState({
      status: 'checking',
      error: undefined,
    });

    Sentry.addBreadcrumb({
      category: 'machineIdAttestation',
      message: 'Checking if attestation is needed',
      level: 'info',
      data: {
        clerkUserId,
      },
    });

    // Check validity once and reuse the result to avoid a duplicate IPC round-trip
    const validation = await checkAttestationValidity(clerkUserId);
    const attestationNeeded = !validation.isValid;

    if (!attestationNeeded) {
      console.log('[AttestationFlow] Attestation not needed, already valid');
      
      machineIdAttestationState.updateState({
        status: 'not_needed',
        lastAttestedAt: validation.attestedAt,
        lastAttestedUserId: validation.attestedUserId,
        expiresAt: validation.expiresAt,
      });

      Sentry.addBreadcrumb({
        category: 'machineIdAttestation',
        message: 'Attestation not needed',
        level: 'info',
        data: {
          clerkUserId,
          attestedAt: validation.attestedAt,
        },
      });

      return;
    }

    console.log('[AttestationFlow] Attestation needed, starting attestation process');
    
    machineIdAttestationState.updateState({
      status: 'attesting',
    });

    Sentry.addBreadcrumb({
      category: 'machineIdAttestation',
      message: 'Starting attestation process',
      level: 'info',
      data: {
        clerkUserId,
      },
    });

    // Perform the attestation
    const result = await performAttestation(
      clerkUserId,
      clerkToken,
      (nonce, userId) => getMachineIdAttestationData(nonce, userId),
    );

    if (result.success) {
      console.log('[AttestationFlow] Attestation successful');
      serviceContainer.analyticsManager.sendMachineIdLinkedEvent();
      
      if (!result.attestedAt) {
        console.warn('[AttestationFlow] Success but no attestedAt timestamp');
        result.attestedAt = Date.now();
      }

      // Store attestation metadata in C++ registry
      await storeAttestationMetadata(
        clerkUserId,
        result.attestedAt,
        result.expiresAt,
      );

      machineIdAttestationState.updateState({
        status: 'attested',
        lastAttestedAt: result.attestedAt,
        lastAttestedUserId: clerkUserId,
        expiresAt: result.expiresAt,
        error: undefined,
      });

      Sentry.captureMessage('Machine ID attestation successful', {
        level: 'info',
        tags: {
          event_type: 'machine_id_attestation_success',
        },
        contexts: {
          attestation: {
            clerkUserId,
            attestedAt: result.attestedAt,
            expiresAt: result.expiresAt,
          },
        },
      });
    } else {
      console.error('[AttestationFlow] Attestation failed:', result.error, result.message);
      
      machineIdAttestationState.updateState({
        status: 'failed',
        error: `${result.error}: ${result.message}`,
      });

      Sentry.captureMessage('Machine ID attestation failed', {
        level: 'warning',
        tags: {
          event_type: 'machine_id_attestation_failure',
          error_code: result.error,
        },
        contexts: {
          attestation: {
            clerkUserId,
            error: result.error,
            message: result.message,
          },
        },
      });
    }
  } catch (error) {
    console.error('[AttestationFlow] Attestation flow error:', error);
    
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    
    machineIdAttestationState.updateState({
      status: 'failed',
      error: errorMessage,
    });

    Sentry.captureException(error, {
      tags: {
        event_type: 'machine_id_attestation_error',
      },
      contexts: {
        attestation: {
          clerkUserId,
        },
      },
    });
  }
}

/**
 * Check-only attestation: verifies whether this machine already has a valid
 * attestation for the given user WITHOUT ever performing a new attestation.
 * 
 * Sets machineIdAttestationState to 'not_needed' if valid, or leaves it at
 * 'idle' if attestation is required (so the Add PC screen can handle it).
 */
export async function checkAttestationOnly(clerkUserId: string): Promise<void> {
  try {
    machineIdAttestationState.updateState({ status: 'checking', error: undefined });

    const validation = await checkAttestationValidity(clerkUserId);

    if (validation.isValid) {
      machineIdAttestationState.updateState({
        status: 'not_needed',
        lastAttestedAt: validation.attestedAt,
        lastAttestedUserId: validation.attestedUserId,
        expiresAt: validation.expiresAt,
      });
    } else {
      machineIdAttestationState.updateState({ status: 'idle' });
    }
  } catch (error) {
    console.error('[AttestationFlow] checkAttestationOnly error:', error);
    machineIdAttestationState.updateState({ status: 'idle' });
  }
}

/**
 * Check if attestation should be triggered based on current state
 * 
 * This can be called periodically or on app startup to ensure attestation is up to date
 */
export function shouldTriggerAttestation(clerkUserId?: string): boolean {
  if (!clerkUserId) {
    return false;
  }

  const state = machineIdAttestationState.currentState;

  // Only block if an attestation check is already in-flight to avoid
  // duplicate concurrent requests.
  if (state.status === 'attesting' || state.status === 'checking') {
    return false;
  }

  // For every other status (idle, failed, attested, not_needed) — always
  // trigger. The real validation happens inside runAttestationFlow →
  // checkAttestationValidity, which reads the C++
  // persisted registry and compares the user+machine combo. Skipping
  // the check here could hide stale in-memory state, expired
  // attestations, or user-switch scenarios that the in-memory snapshot
  // doesn't capture.
  return true;
}

